'use client';

import React from 'react';
import { Badge } from '@/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  ShieldCheck,
  ShieldOff,
  ChevronDown,
  FileText,
  AlertCircle,
  CheckCircle,
  XCircle,
} from 'lucide-react';

export interface MatchedPolicy {
  id: string;
  name: string;
  effect: 'permit' | 'forbid';
}

export interface SimulationResult {
  allowed: boolean;
  reason?: string;
  matchedPolicies?: MatchedPolicy[];
  evaluationOrder?: string[];
  errors?: string[];
}

export interface SimulationDiagnosticsProps {
  result: SimulationResult;
  request?: {
    principal: string;
    action: string;
    resource: string;
    roles?: string[];
  };
}

export function SimulationDiagnostics({ result, request }: SimulationDiagnosticsProps) {
  const [isOpen, setIsOpen] = React.useState(true);

  return (
    <div
      className={`rounded-lg border ${
        result.allowed
          ? 'bg-green-50 border-green-200'
          : 'bg-red-50 border-red-200'
      } animate-in fade-in zoom-in-95`}
    >
      {/* Main Result */}
      <div className="p-4">
        <div className="flex items-center gap-3">
          {result.allowed ? (
            <div className="p-2 rounded-full bg-green-100">
              <ShieldCheck className="h-6 w-6 text-green-600" />
            </div>
          ) : (
            <div className="p-2 rounded-full bg-red-100">
              <ShieldOff className="h-6 w-6 text-red-600" />
            </div>
          )}
          <div>
            <h3
              className={`text-lg font-bold ${
                result.allowed ? 'text-green-700' : 'text-red-700'
              }`}
            >
              {result.allowed ? 'ACCESS GRANTED' : 'ACCESS DENIED'}
            </h3>
            {result.reason && (
              <p className="text-sm text-slate-600 mt-0.5">{result.reason}</p>
            )}
          </div>
        </div>
      </div>

      {/* Detailed Diagnostics */}
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger className="flex items-center justify-between w-full px-4 py-2 border-t text-sm text-slate-600 hover:bg-white/50">
          <span className="font-medium">Diagnostics Details</span>
          <ChevronDown
            className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        </CollapsibleTrigger>
        <CollapsibleContent className="border-t">
          <div className="p-4 space-y-4 bg-white/50">
            {/* Request Summary */}
            {request && (
              <div>
                <h4 className="text-sm font-medium text-slate-700 mb-2">Request</h4>
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div>
                    <span className="text-slate-500">Principal:</span>{' '}
                    <span className="font-mono">{request.principal}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Action:</span>{' '}
                    <Badge variant="outline" className="font-mono ml-1">
                      {request.action}
                    </Badge>
                  </div>
                  <div>
                    <span className="text-slate-500">Resource:</span>{' '}
                    <Badge variant="secondary" className="font-mono ml-1">
                      {request.resource}
                    </Badge>
                  </div>
                </div>
                {request.roles && request.roles.length > 0 && (
                  <div className="mt-2">
                    <span className="text-slate-500 text-sm">Roles:</span>{' '}
                    <div className="inline-flex gap-1 ml-1">
                      {request.roles.map(role => (
                        <Badge key={role} variant="outline" className="capitalize">
                          {role}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Matched Policies */}
            {result.matchedPolicies && result.matchedPolicies.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Matched Policies
                </h4>
                <div className="space-y-2">
                  {result.matchedPolicies.map((policy, idx) => (
                    <div
                      key={policy.id || idx}
                      className="flex items-center gap-2 p-2 bg-slate-50 rounded border"
                    >
                      {policy.effect === 'permit' ? (
                        <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                      ) : (
                        <XCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
                      )}
                      <span className="text-sm font-medium">{policy.name}</span>
                      <Badge
                        variant="outline"
                        className={`ml-auto text-xs ${
                          policy.effect === 'permit'
                            ? 'text-green-600 border-green-200'
                            : 'text-red-600 border-red-200'
                        }`}
                      >
                        {policy.effect}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Evaluation Order */}
            {result.evaluationOrder && result.evaluationOrder.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-slate-700 mb-2">
                  Evaluation Order
                </h4>
                <div className="flex flex-wrap gap-1">
                  {result.evaluationOrder.map((policyName, idx) => (
                    <React.Fragment key={idx}>
                      <Badge variant="outline" className="text-xs">
                        {idx + 1}. {policyName}
                      </Badge>
                      {idx < result.evaluationOrder!.length - 1 && (
                        <span className="text-slate-400">→</span>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}

            {/* Errors */}
            {result.errors && result.errors.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-red-700 mb-2 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  Errors
                </h4>
                <div className="space-y-1">
                  {result.errors.map((error, idx) => (
                    <p key={idx} className="text-sm text-red-600 bg-red-100 p-2 rounded">
                      {error}
                    </p>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
